/**
 * TripPage — Detail public d'un voyage : chapo + carte + sommaire des journees.
 * Route : /voyage/:slug
 */
import { Suspense, lazy, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getTripBySlug } from "../lib/api";
import { Skeleton } from "../components/ui/skeleton";
import { Ship, MapPin, ArrowLeft, ChevronRight } from "lucide-react";

const ItineraryMap = lazy(() => import("../components/ItineraryMap"));

export default function TripPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDay, setActiveDay] = useState(null);

  useEffect(() => {
    setLoading(true);
    getTripBySlug(slug)
      .then((res) => setTrip(res.data))
      .catch(() => setTrip(null))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const onDaySelect = (day) => {
    setActiveDay(day.id);
    if (trip && day.slug) navigate(`/carnets-de-voyage/${trip.slug}/journee/${day.slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary pt-20 p-6">
        <div className="max-w-5xl mx-auto space-y-6">
          <Skeleton className="h-12 w-2/3" />
          <Skeleton className="h-80" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="min-h-screen bg-secondary pt-24 p-6" data-testid="trip-not-found">
        <div className="max-w-md mx-auto text-center">
          <Ship className="h-16 w-16 text-muted-foreground/30 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-semibold text-primary mb-2">Voyage introuvable</h1>
          <Link to="/carnets-de-voyage" className="text-accent underline">Retour aux carnets</Link>
        </div>
      </div>
    );
  }

  const days = trip.days || [];
  const validCoords = days.filter((d) => !d.at_sea && d.coordinates && typeof d.coordinates.lat === "number").length;
  // Bloc 6 : polyline default selon experience_type
  const polylineDefault = ["croisiere", "circuit", "road_trip"].includes(trip.experience_type);
  const showPolyline = typeof trip.show_polyline === "boolean" ? trip.show_polyline : polylineDefault;

  return (
    <div className="min-h-screen bg-secondary pt-20" data-testid="trip-page">
      <section className="bg-white border-b border-border py-12 md:py-16">
        <div className="max-w-5xl mx-auto container-padding">
          <Link
            to="/carnets-de-voyage"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent mb-6"
            data-testid="back-to-trips"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux carnets
          </Link>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-primary mb-3">{trip.title}</h1>
          {trip.subtitle && <p className="text-muted-foreground text-lg mb-4">{trip.subtitle}</p>}
          {trip.chapo && (
            <p className="text-lg italic text-gray-700 border-l-4 border-accent pl-4 mt-6 max-w-3xl">{trip.chapo}</p>
          )}
        </div>
      </section>

      {validCoords >= 1 && (
        <section className="py-8 md:py-12">
          <div className="max-w-5xl mx-auto container-padding">
            <h2 className="font-heading text-2xl font-semibold text-primary mb-4">Itinéraire</h2>
            <Suspense fallback={<div className="h-96 bg-gray-100 rounded animate-pulse" />}>
              <ItineraryMap
                days={days}
                activeDay={activeDay}
                onDaySelect={onDaySelect}
                height={420}
                showPolyline={showPolyline}
                hideIfEmpty={true}
              />
            </Suspense>
          </div>
        </section>
      )}

      <section className="pb-16">
        <div className="max-w-5xl mx-auto container-padding">
          <h2 className="font-heading text-2xl font-semibold text-primary mb-6">Journées</h2>
          {days.length === 0 ? (
            <p className="text-muted-foreground">Aucune journée disponible pour l'instant.</p>
          ) : (
            <div className="space-y-3">
              {days.map((d) => (
                <Link
                  key={d.id}
                  to={`/carnets-de-voyage/${trip.slug}/journee/${d.slug}`}
                  onMouseEnter={() => setActiveDay(d.id)}
                  onMouseLeave={() => setActiveDay(null)}
                  className="flex items-center gap-4 p-4 bg-white border border-border rounded-lg hover:shadow-md hover:border-accent transition-all group"
                  data-testid={`trip-day-item-${d.day_number}`}
                >
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">
                    {d.day_number}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-primary group-hover:text-accent transition-colors">{d.stop_name}</div>
                    {d.date && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {new Date(d.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                      </div>
                    )}
                  </div>
                  {d.coordinates && (
                    <MapPin className="h-4 w-4 text-muted-foreground/60" />
                  )}
                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-accent transition-colors" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
